import { clearReviewDrafts, hasReviewDrafts } from '../app/builder/reviewDraft';
import type { JSX, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AuthenticatedSession } from '@cf/shared';
import type { IAuthClient, MfaChallenge, SignInResult } from './authClient';
import { FixtureAuthClient } from './authClient';
import { clearAllChartViews } from '../charts/liveChartModel';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextValue {
  session: AuthenticatedSession | null;
  /** True while a persisted session is being resumed on app start — the route
   * gate must WAIT rather than bounce to /login and back. */
  restoring: boolean;
  /** True between a successful sign-in and TOTP completion. */
  mfaPending: boolean;
  /** Returns whether the TOTP step is a returning-user code or a first-login
   * enrolment, so the screen can render a QR when enrolling. */
  signIn(email: string, password: string): Promise<SignInResult>;
  /** Returning user: verify a code from an already-enrolled authenticator. The
   * `challenge` (email + session) is threaded from the sign-in result. */
  completeMfa(code: string, challenge?: MfaChallenge | null): Promise<void>;
  /** First login: verify the code that enrols a new authenticator. */
  setupMfa(code: string, challenge?: MfaChallenge | null): Promise<void>;
  /**
   * Mark that an INVITE acceptance has produced an MFA-setup challenge, so the
   * shared MFA screen (which guards on `mfaPending`) renders for a brand-new
   * invitee who never went through `signIn`. The enrolment itself is completed
   * by `setupMfa`, exactly like a first-login user.
   */
  beginInviteEnrolment(): void;
  requestPasswordReset(email: string): Promise<void>;
  confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void>;
  signOut(): Promise<void>;
  /** Current bearer token for authenticated API calls; null before login. */
  getToken(): string | null;
  /**
   * Whether the active auth client actually persists a session across
   * refresh/tabs (i.e. it can `resume`). Screens must not promise "you'll stay
   * signed in" under the fixture/dev seams, which cannot keep it — the promise
   * renders only when the client behind it is the persisting one.
   */
  persistentSession: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  client,
  children,
}: {
  client?: IAuthClient | undefined;
  children: ReactNode;
}): JSX.Element {
  const queryClient = useQueryClient();
  const clientRef = useRef<IAuthClient>(client ?? new FixtureAuthClient());
  const [session, setSession] = useState<AuthenticatedSession | null>(null);
  // Start in the restoring state only when the client can actually resume, so
  // the fixture/dev seams render the login screen instantly as before.
  const [restoring, setRestoring] = useState(clientRef.current.resume !== undefined);
  const [mfaPending, setMfaPending] = useState(false);

  // One resume attempt on mount (guarded so StrictMode's double-invoke cannot
  // race two). A resumed session lands exactly like a completed TOTP step; a
  // null result just means "show the login screen".
  const resumeStartedRef = useRef(false);
  useEffect(() => {
    const resume = clientRef.current.resume?.bind(clientRef.current);
    if (resume === undefined) return;
    if (resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    let cancelled = false;
    void resume()
      .then((next) => {
        if (!cancelled && next !== null) setSession(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    const result = await clientRef.current.signIn(email, password);
    setMfaPending(true);
    return result;
  }, []);

  const completeMfa = useCallback(async (code: string, challenge?: MfaChallenge | null) => {
    const next = await clientRef.current.completeMfa(code, challenge);
    setSession(next);
    setMfaPending(false);
  }, []);

  const setupMfa = useCallback(async (code: string, challenge?: MfaChallenge | null) => {
    const next = await clientRef.current.setupMfa(code, challenge);
    setSession(next);
    setMfaPending(false);
  }, []);

  const beginInviteEnrolment = useCallback(() => {
    setMfaPending(true);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await clientRef.current.requestPasswordReset(email);
  }, []);
  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string) => {
      const client = clientRef.current;
      if (client.confirmPasswordReset === undefined) {
        throw new Error('Password reset is not available in this environment.');
      }
      await client.confirmPasswordReset(email, code, newPassword);
    },
    [],
  );

  // Drafts survive SPA navigation; the warning must survive leaving the editor too.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!hasReviewDrafts()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const signOut = useCallback(async () => {
    clearReviewDrafts();
    await clientRef.current.signOut();
    // Stored chart set-ups are layout preferences only, but their keys embed
    // client ids — on a shared machine they don't outlive the session.
    clearAllChartViews();
    // Drop EVERY cached query: firm-scoped data (clients, team, firm
    // assumption presets) must never survive into the next sign-in, where a
    // different firm's session would read a stale cache before refetching.
    queryClient.clear();
    setSession(null);
    setMfaPending(false);
  }, [queryClient]);

  const getToken = useCallback(() => clientRef.current.getToken(), []);

  const value = useMemo(
    () => ({
      session,
      restoring,
      mfaPending,
      signIn,
      completeMfa,
      setupMfa,
      beginInviteEnrolment,
      requestPasswordReset,
      confirmPasswordReset,
      signOut,
      getToken,
      persistentSession: clientRef.current.resume !== undefined,
    }),
    [
      session,
      restoring,
      mfaPending,
      signIn,
      completeMfa,
      setupMfa,
      beginInviteEnrolment,
      requestPasswordReset,
      signOut,
      getToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
